import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, unref as _unref } from "vue"

const _hoisted_1 = { class: "container justify-stretch flex w-full space-x-3 lg:space-x-10" }
const _hoisted_2 = { class: "h-0.5 w-full bg-gray-400 lg:mb-6" }
const _hoisted_3 = {
  key: 1,
  class: "h-0.5 w-full bg-white opacity-100"
}
const _hoisted_4 = { class: "mb-4 hidden font-bold uppercase lg:block" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("div", { class: "hidden h-0.5 w-10 bg-current transition-colors lg:block" }, null, -1)
const _hoisted_6 = ["src", "type"]
const _hoisted_7 = ["src", "alt"]
const _hoisted_8 = { class: "container pointer-events-auto relative z-10 h-1/3 w-full text-center font-bold" }
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = ["href"]
const _hoisted_11 = {
  key: 0,
  class: "absolute bottom-0 h-2/3 max-h-96 w-full lg:h-[45%]"
}
const _hoisted_12 = { class: "absolute left-0 top-0 h-full w-full" }
const _hoisted_13 = { class: "absolute bottom-0 h-full w-full" }
const _hoisted_14 = ["src", "alt"]

import { computed, onBeforeUnmount, onMounted, provide, ref } from 'vue';
import { CampaignStageItem } from '@/types';
import { getTwConfig, getMimeType, isVideo } from '@/utils';
import BizAi from '@/components/BizAiElement.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'CampaignStage',
  props: {
    id: null,
    kicker: null,
    title: null,
    text: null,
    backgroundMedia: null,
    backgroundMediaAlt: null,
    backgroundMediaAiGenerated: { type: Boolean },
    aiTooltipText: null,
    backgroundOverlay: { type: Boolean },
    image: null,
    imageAlt: null,
    editMode: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
    id: string;
    kicker: string;
    title: string;
    text: string;
    backgroundMedia: string;
    backgroundMediaAlt: string;
    backgroundMediaAiGenerated?: boolean;
    aiTooltipText?: string;
    backgroundOverlay: boolean;
    image: string;
    imageAlt: string;
    editMode: boolean;
}

const SPEED = 5000;

const items = ref<CampaignStageItem[]>([{ ...props }]);
const activeId = ref('');
const activeIndex = ref(-1);
const root = ref<HTMLElement | null>(null);
const oov = ref(false);
let timeout = null;
const showText = ref(true);
const autoplayOn = ref(true);
const isMounted = ref(false);
const twConfig = getTwConfig();

const register = (item: CampaignStageItem) => {
    items.value.push(item);
    setActive(0);
};

function breakpointSize(name: string): number {
    if (Object.prototype.hasOwnProperty.call(twConfig.theme.screens, name)) {
        return parseInt(twConfig.theme.screens[name]);
    }
    return -1;
}

function next() {
    if (activeIndex.value + 1 >= items.value.length && autoplayOn) {
        setActive(0);
    } else if (autoplayOn.value) {
        setActive(activeIndex.value + 1);
    }
}

function setActive(index: number) {
    clearTimeout(timeout);
    activeIndex.value = index;
    activeId.value = items.value[activeIndex.value].id;
    timeout = setTimeout(next, SPEED);
}

function changeItem(index: number) {
    activeIndex.value = index;
    activeId.value = items.value[activeIndex.value].id;
    if (autoplayOn.value) {
        clearTimeout(timeout);
        autoplayOn.value = false;
    }
}

function onItemClicked(id: string) {
    const index = items.value.findIndex(x => x.id === id);
    if (window.innerWidth < breakpointSize('lg') || props.editMode) {
        changeItem(index);
    } else {
        window.location.href = items.value[index].link;
    }
}

function onScroll() {
    if (root.value) {
        if (window.innerHeight + root.value.getBoundingClientRect().y < 0) {
            oov.value = true;
        } else {
            setTimeout(() => {
                oov.value = false;
            }, 150);
        }
    }
}

function replaceWithBr(string) {
    return string.replace(/\n/g, '<br>');
}

function showTextCal() {
    showText.value =
        (window.innerHeight / window.innerWidth > 1.8 && window.innerHeight > 700) ||
        (window.innerHeight / window.innerWidth < 1.4 && window.innerHeight > 750);
}

provide('registerCampaignStageItem', register);
provide('clickCampaignStageItem', onItemClicked);
provide('activeStage', activeId);
provide('activeStageIndex', activeIndex);
provide('autoplayOn', autoplayOn);

const activeItem = computed<CampaignStageItem>(() =>
    activeIndex.value >= 0 && activeIndex.value < items.value.length
        ? items.value[activeIndex.value]
        : null
);

onMounted(() => {
    window.addEventListener('scroll', onScroll);
    showTextCal();
    window.addEventListener('resize', showTextCal);
    isMounted.value = true;
});

onBeforeUnmount(() => {
    clearTimeout(timeout);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', showTextCal);
});

return (_ctx: any,_cache: any) => {
  const _component_progress_bar = _resolveComponent("progress-bar")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: "flex h-px min-h-screen flex-col bg-gray-500 text-white",
      ref_key: "root",
      ref: root
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["relative z-10 lg:h-24", [__props.editMode ? 'mb-12' : 'mt-20 lg:mt-16']])
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (changeItem(0))),
            class: _normalizeClass(["flex-1 cursor-pointer transition-colors lg:pt-5", [activeIndex.value === 0 ? 'text-white' : 'text-gray-400']])
          }, [
            _createElementVNode("div", _hoisted_2, [
              (autoplayOn.value)
                ? (_openBlock(), _createBlock(_component_progress_bar, {
                    key: 0,
                    "actual-index": activeIndex.value + 1,
                    slide: 0,
                    contrast: ""
                  }, null, 8, ["actual-index"]))
                : (activeIndex.value === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3))
                  : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_4, _toDisplayString(__props.kicker), 1),
            _hoisted_5
          ], 2),
          _renderSlot(_ctx.$slots, "default")
        ])
      ], 2),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: item.id,
          class: _normalizeClass(["pointer-events-none absolute flex h-full w-full flex-col", { 'overflow-hidden': __props.editMode }])
        }, [
          _createElementVNode("div", {
            class: _normalizeClass(["lg:gray-500 absolute h-full w-full flex-1 pt-32 transition-opacity duration-1000 lg:pt-56", [activeId.value === item.id ? 'opacity-100' : 'opacity-0']])
          }, [
            (item.backgroundMedia)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["absolute bottom-0 left-0 w-full overflow-hidden", [item.image ? 'top-3/4 lg:top-[65%]' : 'top-0']]),
                  style: {"clip-path":"polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"}
                }, [
                  (_unref(isVideo)(item.backgroundMedia) && item.backgroundMediaAiGenerated)
                    ? (_openBlock(), _createBlock(BizAi, {
                        key: 0,
                        type: "icon",
                        show: item.backgroundMediaAiGenerated,
                        position: "top-right",
                        "position-mobile": "top-right",
                        "z-index": "z-20",
                        "tooltip-text": __props.aiTooltipText,
                        "tooltip-position": "bottom",
                        "icon-color": "text-white"
                      }, null, 8, ["show", "tooltip-text"]))
                    : _createCommentVNode("", true),
                  (_unref(isVideo)(item.backgroundMedia || __props.backgroundMedia))
                    ? (_openBlock(), _createElementBlock("video", {
                        key: 1,
                        class: _normalizeClass(["top-0 h-full w-full object-cover", [__props.editMode ? 'absolute' : 'fixed']]),
                        autoplay: "",
                        loop: "",
                        playsinline: "",
                        muted: ""
                      }, [
                        _createElementVNode("source", {
                          src: item.backgroundMedia || __props.backgroundMedia,
                          type: _unref(getMimeType)(item.backgroundMedia || __props.backgroundMedia)
                        }, null, 8, _hoisted_6)
                      ], 2))
                    : (_openBlock(), _createElementBlock("img", {
                        key: 2,
                        src: item.backgroundMedia || __props.backgroundMedia,
                        alt: item.backgroundMediaAlt || __props.backgroundMediaAlt,
                        class: _normalizeClass([[__props.editMode ? 'absolute' : 'fixed'], "top-0 h-full w-full object-cover"])
                      }, null, 10, _hoisted_7))
                ], 2))
              : _createCommentVNode("", true),
            (__props.backgroundOverlay && item.backgroundMedia)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: _normalizeClass([[item.image ? 'top-3/4 lg:top-[65%]' : 'top-0'], "absolute bottom-0 left-0 w-full overflow-hidden bg-gray-500 opacity-10"])
                }, null, 2))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_8, [
              (index === 0)
                ? (_openBlock(), _createElementBlock("h1", {
                    key: 0,
                    class: _normalizeClass({
                            'text-3xl lg:text-[4.125rem] lg:leading-[4.5rem]':
                                _unref(activeItem) && _unref(activeItem).title && _unref(activeItem).title.length > 25,
                            'text-6xl leading-[4.5rem] lg:text-9xl':
                                _unref(activeItem) && _unref(activeItem).title && _unref(activeItem).title.length <= 25
                        })
                  }, _toDisplayString(_unref(activeItem) ? replaceWithBr(_unref(activeItem).title) : replaceWithBr(__props.title)), 3))
                : (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: _normalizeClass({
                            'text-3xl lg:text-[4.125rem] lg:leading-[4.5rem]':
                                _unref(activeItem) && _unref(activeItem).title && _unref(activeItem).title.length > 25,
                            'text-6xl leading-[4.5rem] lg:text-9xl':
                                _unref(activeItem) && _unref(activeItem).title && _unref(activeItem).title.length <= 25
                        })
                  }, _toDisplayString(_unref(activeItem) ? replaceWithBr(_unref(activeItem).title) : replaceWithBr(__props.title)), 3)),
              (showText.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 2,
                    class: "mt-4 lg:text-lg",
                    innerHTML: 
                            _unref(activeItem) ? replaceWithBr(_unref(activeItem).text) : replaceWithBr(__props.text)
                        
                  }, null, 8, _hoisted_9))
                : _createCommentVNode("", true),
              (_unref(activeItem) && _unref(activeItem).link && _unref(activeItem).linkLabel)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 3,
                    href: _unref(activeItem).link,
                    class: "button button-primary--light mt-8"
                  }, _toDisplayString(_unref(activeItem).linkLabel), 9, _hoisted_10))
                : _createCommentVNode("", true)
            ])
          ], 2),
          (item.image)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _createElementVNode("div", {
                  class: _normalizeClass(["absolute bottom-0 left-0 z-10 h-full w-full", [activeId.value === item.id ? 'opacity-100' : 'opacity-0']])
                }, [
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("img", {
                        src: item.image || __props.image,
                        alt: item.imageAlt || __props.imageAlt,
                        class: "mx-auto h-full w-5/6 object-contain object-bottom lg:w-full"
                      }, null, 8, _hoisted_14)
                    ])
                  ])
                ], 2)
              ]))
            : _createCommentVNode("", true)
        ], 2))
      }), 128))
    ], 512),
    _renderSlot(_ctx.$slots, "buttons", {
      oov: oov.value,
      visible: activeIndex.value === 0
    }),
    (!isMounted.value)
      ? _renderSlot(_ctx.$slots, "title", { key: 0 })
      : _createCommentVNode("", true)
  ], 64))
}
}

})